import { registerPlugin } from '@capacitor/core';
import { isCapacitorNative, isPlatformAndroid, isPlatformIOS } from 'environments/environment-helpers';
import { ensureImageSizeAsync } from 'services/image-utils';
import { timeout } from '@cf-shared/shared-util';
import { CameraService } from 'services/camera.service';
import { BarcodeDecoderService, QuaggaProcessResult } from 'services/barcode-decoder.service';

export type PermissionStatus = 'granted' | 'denied' | 'prompt';
export type FacingMode = 'environment' | 'user';

export type RectCssPx = { x: number; y: number; width: number; height: number };

export interface StartOptions {
  facingMode: FacingMode;
  aspectRatio: '1:1' | '4:3' | 'auto';
  idealResolution: number;
  /** Native only — position of the preview layer behind the WebView (CSS px). */
  frameRect?: RectCssPx;
  /** Web only — open this one camera instead of letting the browser pick by
   *  `facingMode`. The PWA camera selector and the rotation through the
   *  device list both need a named camera. Ignored by native strategies. */
  deviceId?: string | null;
  /** Web only — reject with a `TimeoutError` if the browser has not opened the
   *  camera by then. Covers the open only, not the zoom / flash setup after it.
   *  Absent or 0 means no limit (a first visit must leave time for the
   *  permission dialog). Ignored by native strategies. */
  openTimeoutMs?: number;
}

/** State of the native overlay controls (flash / flip / zoom pills) that the
 *  plugin renders above the preview layer on iOS. Pushed from `cs-camera-view`
 *  whenever the underlying `CameraService` state changes. Noop on web. */
export interface OverlayState {
  flashVisible: boolean;
  flashOn: boolean;
  flipVisible: boolean;
  zoomVisible: boolean;
  zoomLabel: string;
}

export type OverlayTap = 'flash' | 'flip' | 'zoomReset' | 'zoomStop';

export interface CaptureOptions {
  aspectRatio: '1:1' | '4:3' | 'auto';
  maxImageSize: number;
  /** Web-only — video element to grab the frame from. Ignored by native. */
  video?: HTMLVideoElement;
}

/** Confirmed barcode emission from a strategy's decoder. `type` is the decoder's
 *  own format name (e.g. `'ean_13'` on Quagga, `'ean13'` from AVFoundation —
 *  consumers treat it as opaque). `bounds` is a best-effort fractional-space
 *  rect (0..1 on each axis) when the decoder reports one. */
export interface BarcodeDetectionResult {
  value: string;
  type: string;
  bounds?: { x: number; y: number; w: number; h: number };
}

export interface StartBarcodeDetectionOptions {
  /** Web only — DOM element Quagga inserts its `<video>` + overlay canvases
   *  into. Ignored by native strategies, which render via their own preview
   *  layers. */
  target?: Element;
  /** Prefer this deviceId; null means "let the browser pick via facingMode". */
  deviceId: string | null;
  /** Requested barcode formats. Web (Quagga) currently ignores this and
   *  always decodes EAN/UPC; native strategies honor it when they land. */
  types: string[];
  /** Confirmed-code callback (wired to the directive's `(detected)` emitter
   *  by `cs-camera-view`). Fires per Quagga detection on web — the page
   *  applies its own successive-match confirmation heuristic on top. */
  onDetected: (result: BarcodeDetectionResult) => void;
  /** Web only — per-frame callback (raw Quagga result). Pages use it to run
   *  false-positive heuristics and draw their own detection overlay. */
  onProcessed?: (result: QuaggaProcessResult) => void;
  /** Relax Quagga's input constraints — used by the OverconstrainedError
   *  retry path in `initCameraWithRotationAsync`. */
  relaxedConstraints?: boolean;
  /** Caller destruction check — lets the decoder bail if the host component
   *  was destroyed before Quagga's async init resolved. */
  isDestroyed?: () => boolean;
}

export interface StartResult {
  /** Present on web so callers can wire `srcObject`. Null on native, and
   *  null on web when a later `start()` or `stop()` superseded this call
   *  while the browser was still opening the camera or setting it up — the
   *  caller has moved on, and this call's stream has already been stopped. */
  stream: MediaStream | null;
  facingMode: FacingMode;
}

export interface CameraStrategy {
  readonly isNative: boolean;

  /** Normalized slider position (0..1) that maps to the device's default
   *  "1× wide" zoom. Native reports this from the camera's
   *  `virtualDeviceSwitchOverVideoZoomFactors.first`; web has no equivalent
   *  and reports 0. */
  readonly initialZoomLevel: number;

  /** Capability flag consumed by `cs-camera-view`'s content-child detection:
   *  when a `<cs-barcode-detector>` is projected and the preferred strategy
   *  reports `false`, the component instantiates a `WebCameraStrategy` for
   *  itself so Quagga keeps decoding. */
  readonly supportsBarcodeDetection: boolean;

  // permission
  requestPermission(): Promise<PermissionStatus>;
  getPermissionStatus(): Promise<PermissionStatus>;

  // lifecycle
  start(options: StartOptions): Promise<StartResult>;
  stop(): Promise<void>;
  flip(): Promise<StartResult>;

  // capture
  capture(options: CaptureOptions): Promise<{ dataUrl: string; base64: string }>;

  // controls
  setZoom(level: number): Promise<void>;
  toggleFlash(): Promise<boolean>;

  // capability queries (reflect currently-started camera)
  getZoomLimits(): { min: number; max: number } | null;
  getFlashSupported(): boolean;

  // barcode detection (guarded by supportsBarcodeDetection)
  startBarcodeDetection(options: StartBarcodeDetectionOptions): Promise<void>;
  /** Tears down any active barcode pipeline. On web, optionally accept the
   *  container Quagga rendered into so the injected `<video>`'s `srcObject`
   *  is released in the same step — without this, mid-rotation retries leak
   *  stream references until the whole camera-view unmounts. */
  stopBarcodeDetection(container?: Element | Document): Promise<void>;
  /** Decoder source-frame dimensions (web only — native strategies use
   *  preview-layer-space coordinates that don't need a separate transform).
   *  Returns null when no decoder is active OR on native strategies. The
   *  bracket-tracker overlay reads this per-frame to map decoder-space
   *  rects into DOM pixel space. */
  getDecoderSourceDims(): { width: number; height: number } | null;

  // native-only; noop on web
  updateFrame(rect: RectCssPx): Promise<void>;

  /** Push overlay state to the native UI (flash/flip/zoom pills). Noop on web. */
  setOverlayState(state: OverlayState): Promise<void>;
  /** Subscribe to tap events from native overlay controls. Returns an
   *  unsubscribe function. Noop on web (handler never fires). */
  onOverlayTap(handler: (tap: OverlayTap, ratio?: number) => void): () => void;
}

// ── Native plugin interface ────────────────────────────────────────────

interface CsCameraPreviewPluginInterface {
  start(options: { x: number; y: number; width: number; height: number; facingMode: FacingMode }): Promise<{ facingMode: FacingMode }>;
  stop(): Promise<void>;
  flip(): Promise<{ facingMode: FacingMode }>;
  capture(options: { aspectRatio: string; quality: number }): Promise<{ base64: string; width: number; height: number }>;
  setZoom(options: { level: number }): Promise<void>;
  getZoomLimits(): Promise<{ min: number; max: number; current?: number }>;
  toggleFlash(options: { on: boolean }): Promise<{ on: boolean }>;
  getFlashSupported(): Promise<{ supported: boolean }>;
  requestPermission(): Promise<{ status: PermissionStatus }>;
  getPermissionStatus(): Promise<{ status: PermissionStatus }>;
  updateFrame(options: { x: number; y: number; width: number; height: number }): Promise<void>;
  setOverlayState(state: OverlayState): Promise<void>;
  startBarcodeDetection(options: { types: string[] }): Promise<void>;
  stopBarcodeDetection(): Promise<void>;
  addListener(event: 'overlayTap', handler: (payload: { button: OverlayTap; ratio?: number }) => void): Promise<{ remove: () => Promise<void> }>;
  addListener(event: 'barcodeDetected', handler: (payload: { value: string; type: string; bounds?: { x: number; y: number; w: number; h: number } }) => void): Promise<{ remove: () => Promise<void> }>;
}

const CsCameraPreviewPlugin = registerPlugin<CsCameraPreviewPluginInterface>('CsCameraPreviewPlugin');

// ── Native strategy (iOS and Android) ─────────────────────────────────

/**
 * Bridges `cs-camera-view` to the `CsCameraPreviewPlugin` Capacitor plugin on
 * both native platforms. The plugin has one JS interface and two native
 * implementations: AVFoundation on iOS
 * (`client/ios-overlay/App/App/CsCameraPreviewPlugin.swift`) and CameraX +
 * ML Kit (bundled model) on Android
 * (`client/android-overlay/app/src/main/java/app/mhphysique/fitness/CsCameraPreviewPlugin.java`).
 * The architecture is in `design-docs/native-camera-preview.md`.
 *
 * Both platforms decode barcodes natively, so `cs-camera-view`'s
 * capability-aware routing picks this strategy for the barcode page on
 * native (no Quagga).
 *
 * This used to be two classes, one per platform. They became identical code,
 * so they were merged (df-a9b84b0df038). `logName` keeps each platform's log
 * prefix as it was (`NativeCameraStrategy` on iOS, `NativeAndroidCameraStrategy`
 * on Android), so existing log searches still match. If a platform ever needs
 * different behaviour, give it a subclass then.
 */
export class NativePluginCameraStrategy implements CameraStrategy {
  readonly isNative = true;
  readonly supportsBarcodeDetection = true;

  private facing: FacingMode = 'environment';
  private isFlashOn = false;
  private isFlashSupported = false;
  private zoomLimits: { min: number; max: number } | null = null;
  /** Initial normalized zoom slider position — matches the 1x wide lens. */
  initialZoomLevel = 0;

  /** Active `barcodeDetected` unsubscribe. Held so `stopBarcodeDetection`
   *  can tear it down before issuing the native stop. */
  private barcodeUnsub: (() => void) | null = null;

  /** @param logName prefix for this strategy's log lines, naming the platform */
  constructor(private readonly logName: string) {}

  async requestPermission(): Promise<PermissionStatus> {
    const { status } = await CsCameraPreviewPlugin.requestPermission();
    return status;
  }

  async getPermissionStatus(): Promise<PermissionStatus> {
    const { status } = await CsCameraPreviewPlugin.getPermissionStatus();
    return status;
  }

  async start(options: StartOptions): Promise<StartResult> {
    const rect = options.frameRect ?? { x: 0, y: 0, width: 0, height: 0 };
    const { facingMode } = await CsCameraPreviewPlugin.start({
      x: rect.x,
      y: rect.y,
      width: rect.width,
      height: rect.height,
      facingMode: options.facingMode,
    });
    this.facing = facingMode;
    this.isFlashOn = false;
    await this.syncCapabilitiesAsync();
    return { stream: null, facingMode: this.facing };
  }

  async stop(): Promise<void> {
    try {
      await CsCameraPreviewPlugin.stop();
    } finally {
      this.isFlashOn = false;
    }
  }

  async flip(): Promise<StartResult> {
    const { facingMode } = await CsCameraPreviewPlugin.flip();
    this.facing = facingMode;
    this.isFlashOn = false;
    await this.syncCapabilitiesAsync();
    return { stream: null, facingMode: this.facing };
  }

  async capture(options: CaptureOptions): Promise<{ dataUrl: string; base64: string }> {
    const { base64 } = await CsCameraPreviewPlugin.capture({
      aspectRatio: options.aspectRatio,
      quality: 0.85,
    });
    const dataUrl = `data:image/jpeg;base64,${base64}`;
    if (base64.length <= options.maxImageSize) {
      return { dataUrl, base64 };
    }
    // if oversized — decode to canvas and run the existing progressive resizer
    return await resizeBase64ToLimit(dataUrl, options.maxImageSize);
  }

  async setZoom(level: number): Promise<void> {
    await CsCameraPreviewPlugin.setZoom({ level: Math.max(0, Math.min(1, level)) });
  }

  async toggleFlash(): Promise<boolean> {
    const next = !this.isFlashOn;
    const { on } = await CsCameraPreviewPlugin.toggleFlash({ on: next });
    this.isFlashOn = on;
    return on;
  }

  getZoomLimits() { return this.zoomLimits; }
  getFlashSupported() { return this.isFlashSupported; }

  async updateFrame(rect: RectCssPx): Promise<void> {
    await CsCameraPreviewPlugin.updateFrame(rect);
  }

  async setOverlayState(state: OverlayState): Promise<void> {
    await CsCameraPreviewPlugin.setOverlayState(state);
  }

  async startBarcodeDetection(options: StartBarcodeDetectionOptions): Promise<void> {
    // if a previous subscription is still live (rapid reinit), tear it down
    // before installing a new one. stopBarcodeDetection on the plugin is
    // idempotent; calling it first is simpler than tracking "was running".
    if (this.barcodeUnsub) {
      this.barcodeUnsub();
      this.barcodeUnsub = null;
    }
    // Idempotent pre-stop: if the plugin had no active detector, it resolves;
    // if it rejects (e.g. session torn down between a prior stop and this call)
    // we log and continue — the upcoming start will install a fresh one.
    await CsCameraPreviewPlugin.stopBarcodeDetection().catch(err => {
      console.log(`${this.logName}: pre-start stopBarcodeDetection rejected (expected if no prior session):`, err);
    });

    // Subscribe before starting — an early detection would otherwise miss us.
    // Capacitor's addListener is idempotent re: order but listener-install is
    // async. We save+listen and only call startBarcodeDetection after the sub
    // is in place to keep the window tight. See WebCameraStrategy's stop-
    // before-start pattern in this file for the mirror image.
    let active = true;
    const sub = await CsCameraPreviewPlugin.addListener('barcodeDetected', ({ value, type, bounds }) => {
      if (!active) return;
      options.onDetected({ value, type, bounds });
      // Synthesize a Quagga-shaped per-frame event so barcode-scanner-page's
      // existing `onFrameProcessed` confirmation + hint heuristics keep
      // working with zero page changes. Neither AVFoundation's metadata
      // callback nor ML Kit fires every frame — only when a code is visible —
      // so we emit only on detection, which is sufficient for the
      // successive-match counter.
      options.onProcessed?.({
        codeResult: { code: value, format: type },
        boxes: bounds ? [[[bounds.x, bounds.y], [bounds.x + bounds.w, bounds.y + bounds.h]]] : undefined,
      });
    });
    const removeFn = () => sub.remove();
    this.barcodeUnsub = () => { active = false; void removeFn(); };

    try {
      await CsCameraPreviewPlugin.startBarcodeDetection({ types: options.types });
    } catch (err) {
      // roll back the subscription if the native start fails
      active = false;
      void removeFn();
      this.barcodeUnsub = null;
      throw err;
    }

    // Emit one synthetic empty `processed` immediately so the page's
    // `cameraStartedTime` gets seeded. Neither native decoder calls back
    // while no code is visible, so no further frame arrives while nothing is
    // in view; the page arms its own timer at this seed to run the
    // "no code for 10s" bad-camera check (#6360).
    options.onProcessed?.(undefined);
  }

  /** `container` is part of the shared `CameraStrategy` interface for the
   *  web decoder's `<video>` srcObject release on Quagga teardown; the
   *  native strategy has no DOM to clean up (the preview lives in the
   *  native layer — `AVCaptureVideoPreviewLayer` on iOS, an overlay
   *  `FrameLayout` on Android — torn down by `stop()`), so the parameter
   *  is intentionally unused here. */
  async stopBarcodeDetection(_container?: Element | Document): Promise<void> {
    if (this.barcodeUnsub) {
      this.barcodeUnsub();
      this.barcodeUnsub = null;
    }
    await CsCameraPreviewPlugin.stopBarcodeDetection();
  }

  // Native strategies have no JS decoder; see interface JSDoc.
  getDecoderSourceDims(): { width: number; height: number } | null { return null; }

  onOverlayTap(handler: (tap: OverlayTap, ratio?: number) => void): () => void {
    let active = true;
    let removeFn: (() => Promise<void>) | null = null;
    CsCameraPreviewPlugin.addListener('overlayTap', ({ button, ratio }) => {
      if (active) handler(button, ratio);
    }).then(sub => {
      if (!active) { void sub.remove(); return; }
      // wrap in an arrow to preserve `sub` as the callee — bare method refs
      // are vulnerable to binding changes in future Capacitor releases
      removeFn = () => sub.remove();
    }).catch(err => {
      // Terminal failure — overlay taps will never fire for the rest of this
      // session. Use console.error so the log level matches the severity.
      console.error(`${this.logName}: overlayTap listener registration failed:`, err);
    });
    return () => {
      active = false;
      if (removeFn) void removeFn();
    };
  }

  /** Re-read flash support + zoom limits after a camera (re)start or flip,
   *  then map the reported `current` zoom onto the normalized 0..1 slider
   *  position the UI exposes. */
  private async syncCapabilitiesAsync(): Promise<void> {
    const [{ supported }, limits] = await Promise.all([
      CsCameraPreviewPlugin.getFlashSupported(),
      CsCameraPreviewPlugin.getZoomLimits(),
    ]);
    this.isFlashSupported = supported;
    this.zoomLimits = limits.max > limits.min ? { min: limits.min, max: limits.max } : null;
    this.initialZoomLevel = computeInitialZoomLevel(this.zoomLimits, limits.current);
  }
}

/** Normalize the plugin's reported `current` display-zoom factor onto the
 *  0..1 slider position the UI exposes. Returns 0 when limits are missing
 *  or degenerate. */
function computeInitialZoomLevel(
  zoomLimits: { min: number; max: number } | null,
  current: number | undefined,
): number {
  if (!zoomLimits || typeof current !== 'number') return 0;
  const range = zoomLimits.max - zoomLimits.min;
  if (range <= 0) return 0;
  return Math.max(0, Math.min(1, (current - zoomLimits.min) / range));
}

// ── Web strategy ──────────────────────────────────────────────────────

/**
 * Thin adapter over `CameraService` so `cs-camera-view` can call the same
 * interface on web and native. Camera state (zoom limits, flash support)
 * still lives on `CameraService` so the barcode scanner can read it.
 */
export class WebCameraStrategy implements CameraStrategy {
  readonly isNative = false;
  /** Web has no multi-lens "wide baseline" concept — zoom starts at 0. */
  readonly initialZoomLevel = 0;
  /** Quagga handles barcode decoding on every non-iOS-native path. */
  readonly supportsBarcodeDetection = true;

  private activeStream: MediaStream | null = null;
  private barcodeDecoder: BarcodeDecoderService | null = null;
  /** Bumped by every `start()` and `stop()`. A `start()` whose number is no
   *  longer current when the browser hands its stream back has been
   *  superseded — its caller stopped the camera or started another one —
   *  so it must not replace the stream the caller moved on to. */
  private startGeneration = 0;

  constructor(private readonly cameraService: CameraService) {}

  async requestPermission(): Promise<PermissionStatus> {
    await this.cameraService.waitForPermissionAsync();
    return this.cameraService.permissionStatus?.state ?? 'prompt';
  }

  async getPermissionStatus(): Promise<PermissionStatus> {
    await this.cameraService.waitForPermissionAsync();
    return this.cameraService.permissionStatus?.state ?? 'prompt';
  }

  /** Opens the camera and makes its stream the one every other method acts on.
   *  Does NOT stop the previous stream: the camera-switch animation keeps the
   *  old stream on screen until the new one is showing, and stops it itself. */
  async start(options: StartOptions): Promise<StartResult> {
    const generation = ++this.startGeneration;
    this.cameraService.activeFacingMode = options.facingMode;
    const constraints = buildWebConstraints(options.aspectRatio, options.idealResolution);
    // a null deviceId lets the browser pick by facingMode
    const opening = this.cameraService.openCameraAsync(options.deviceId ?? null, constraints);
    let stream: MediaStream;
    try {
      // only the browser's open is timed; the zoom / flash setup below is not
      stream = await (options.openTimeoutMs ? timeout(options.openTimeoutMs, opening) : opening);
    } catch (err) {
      // if the open timed out, the browser may still hand the camera over later:
      // release it then. A later rejection has nothing left to report, because the
      // caller already has this error
      opening.then(late => late.getTracks().forEach(t => t.stop()), () => undefined);
      throw err;
    }
    // if the caller moved on while the browser was opening (a stop or a newer
    // start), release this camera instead of replacing the current stream
    if (generation !== this.startGeneration) {
      stream.getTracks().forEach(t => t.stop());
      return { stream: null, facingMode: options.facingMode };
    }
    this.activeStream = stream;
    const track = stream.getVideoTracks()[0];
    if (track) {
      await this.cameraService.setupTrackCapabilitiesAsync(track);
    }
    // if the caller moved on during setup, this camera is no longer the one to show
    if (generation !== this.startGeneration) {
      stream.getTracks().forEach(t => t.stop());
      if (this.activeStream === stream) this.activeStream = null;
      return { stream: null, facingMode: options.facingMode };
    }
    return { stream, facingMode: options.facingMode };
  }

  async stop(): Promise<void> {
    this.startGeneration++;
    if (this.activeStream) {
      this.activeStream.getTracks().forEach(t => t.stop());
      this.activeStream = null;
    }
  }

  async flip(): Promise<StartResult> {
    const next: FacingMode = this.cameraService.activeFacingMode === 'environment' ? 'user' : 'environment';
    const oldStream = this.activeStream;
    try {
      return await this.start({
        facingMode: next,
        // aspect/resolution preserved by caller in subsequent re-frames;
        // for a flip we don't have the original options, so re-use the current
        // track's dimensions by leaving aspectRatio 'auto' and idealResolution 0.
        aspectRatio: 'auto',
        idealResolution: 0,
      });
    } finally {
      oldStream?.getTracks().forEach(t => t.stop());
    }
  }

  async capture(options: CaptureOptions): Promise<{ dataUrl: string; base64: string }> {
    const video = options.video;
    if (!video) throw new Error('WebCameraStrategy.capture requires a video element');

    const canvas = document.createElement('canvas');
    const vw = video.videoWidth;
    const vh = video.videoHeight;

    if (options.aspectRatio === '1:1') {
      const size = Math.min(vw, vh);
      const sx = (vw - size) / 2;
      const sy = (vh - size) / 2;
      canvas.width = size;
      canvas.height = size;
      canvas.getContext('2d').drawImage(video, sx, sy, size, size, 0, 0, size, size);
    } else {
      canvas.width = vw;
      canvas.height = vh;
      canvas.getContext('2d').drawImage(video, 0, 0);
    }

    return ensureImageSizeAsync(canvas, options.maxImageSize);
  }

  async setZoom(level: number): Promise<void> {
    this.cameraService.zoomLevel = Math.max(0, Math.min(1, level));
    const track = this.activeStream?.getVideoTracks()[0];
    if (track) await this.cameraService.adjustZoomAsync(track);
  }

  async toggleFlash(): Promise<boolean> {
    const track = this.activeStream?.getVideoTracks()[0];
    if (!track) return this.cameraService.flashEnabled;
    await this.cameraService.toggleFlashAsync(track);
    return this.cameraService.flashEnabled;
  }

  getZoomLimits() { return this.cameraService.zoomLimits ?? null; }
  getFlashSupported() { return this.cameraService.flashSupported; }

  async updateFrame(): Promise<void> {
    // web has no overlay to reposition
  }

  async setOverlayState(): Promise<void> {
    // web renders overlay buttons from the DOM
  }

  onOverlayTap(_handler: (tap: OverlayTap, ratio?: number) => void): () => void {
    // no native overlay on web → never fires
    return () => undefined;
  }

  /** Expose the active stream so `cs-camera-view` can assign it to the <video>. */
  getActiveStream(): MediaStream | null {
    return this.activeStream;
  }

  /** Expose the active track (Quagga's track in barcode mode, getUserMedia's
   *  track in photo mode). `cs-camera-view.getActiveTrack()` delegates here. */
  getActiveTrack(): MediaStreamTrack | null {
    if (this.barcodeDecoder) return this.barcodeDecoder.getActiveTrack();
    return this.activeStream?.getVideoTracks()[0] ?? null;
  }

  async startBarcodeDetection(options: StartBarcodeDetectionOptions): Promise<void> {
    if (!options.target) {
      throw new Error('WebCameraStrategy.startBarcodeDetection: target element is required');
    }
    // if a prior decode session is active, tear it down before starting a new
    // one — this keeps the Quagga-owned stream from leaking on retry/rotate.
    // pass the same target so the injected <video>'s srcObject is released.
    if (this.barcodeDecoder) {
      await this.barcodeDecoder.stopDecoding(options.target);
      this.barcodeDecoder = null;
    }

    this.barcodeDecoder = new BarcodeDecoderService(this.cameraService.logging);
    await this.barcodeDecoder.startDecoding({
      target: options.target,
      deviceId: options.deviceId,
      onDetected: options.onDetected,
      onProcessed: options.onProcessed,
      relaxedConstraints: options.relaxedConstraints,
      isDestroyed: options.isDestroyed,
    });

    const track = this.barcodeDecoder.getActiveTrack();
    if (!track) {
      // decoder bailed (DOM not ready or caller destroyed) — leave state clean
      return;
    }

    // if opened with facingMode (first visit), enumerate cameras now that
    // permission is granted — preserves the pre-migration behavior where
    // `startBarcodeScannerAsync` called `populateCamerasFromTrackAsync`
    if (!options.deviceId) {
      await this.cameraService.populateCamerasFromTrackAsync(track);
    }

    // setup capabilities (zoom, flash) on the Quagga track
    await this.cameraService.setupTrackCapabilitiesAsync(track);

    // optimize camera dimensions — ask for enough pixels to decode reliably.
    // The `aspectRatio: 1` anchor is critical: without it, asking for
    // `width: {min: N}, height: {min: N}` rules out every 16:9 mode (height
    // < N) and most 4:3 modes, so the upgrade is rejected with
    // OverconstrainedError, swallowed by `applyTrackConstraintsAsync`, and
    // Quagga is stuck decoding the original `Quagga.init` open of 800×800
    // (which the camera resolves to ~640×480 on most devices) — far too
    // low for reliable EAN/UPC decoding. With the anchor, the camera
    // delivers a square crop of its native mode (e.g. 1080×1080 from
    // 1920×1080) which Quagga can decode cleanly. The barcode-scanner
    // viewport is already square (page Input `aspectRatio: '1:1'`), so a
    // 1:1 source painted with object-fit: cover renders 1:1 with no extra
    // crop. (This was dropped during the cs-camera-view migration on the
    // assumption that a square track caused zoomed/cropped output via
    // cover-fit — that assumption was incorrect for this page.)
    if (track.getCapabilities) {
      const caps = track.getCapabilities() as { width?: { max?: number }; height?: { max?: number } };
      const wMax = caps.width?.max;
      const hMax = caps.height?.max;
      // require both dimensions to expose a numeric max, otherwise the
      // -100/min math below produces NaN or negatives that
      // applyTrackConstraintsAsync would silently swallow as
      // OverConstrainedError — same regression mode this hotfix fixes.
      if (typeof wMax === 'number' && typeof hMax === 'number') {
        const minDim = Math.max(0, Math.min(wMax - 100, hMax - 100));
        const desiredDim = Math.min(2000, minDim);
        if (desiredDim > 0) {
          await this.cameraService.applyTrackConstraintsAsync(track, {
            aspectRatio: 1,
            width: { min: desiredDim },
            height: { min: desiredDim },
          });
          // Diagnostic — confirms the upgrade actually applied. Without
          // the aspectRatio anchor above this used to fail silently,
          // leaving Quagga at sub-megapixel resolution. Keep this log
          // until we have confidence across the device matrix.
          const settings = track.getSettings() as { width?: number; height?: number; aspectRatio?: number };
          console.log('Quagga track settings after upgrade:', settings);
        } else {
          // desiredDim <= 0 means the camera reports a max below 100 on
          // at least one dim — surface the cap shape so a device-matrix
          // bug shows up in logs instead of silently leaving Quagga at
          // its 800-class init resolution.
          console.log('Quagga track caps yield non-positive desiredDim — skipping resolution upgrade:', { wMax, hMax });
        }
      } else {
        // Camera doesn't expose numeric width.max/height.max — same
        // diagnostic gap, surfaced.
        console.log('Quagga track caps missing numeric width/height max — skipping resolution upgrade:', caps);
      }
    }

    // expose the Quagga track via activeStream so setZoom/toggleFlash keep
    // working against it without a separate barcode-mode branch
    this.activeStream = new MediaStream([track]);
  }

  async stopBarcodeDetection(container?: Element | Document): Promise<void> {
    if (this.barcodeDecoder) {
      await this.barcodeDecoder.stopDecoding(container);
      this.barcodeDecoder = null;
    }
    // drop the MediaStream wrapper so later `getActiveTrack()` calls return null
    this.activeStream = null;
  }

  /** Decoder source-frame dimensions from the underlying Quagga decoder.
   *  Routes through `BarcodeDecoderService` so callers (cs-camera-view +
   *  the bracket-tracker overlay pipeline) don't reach into Quagga's
   *  global state directly. Null before the decoder is started or after
   *  `stopBarcodeDetection`. */
  getDecoderSourceDims(): { width: number; height: number } | null {
    return this.barcodeDecoder?.getSourceDims() ?? null;
  }
}

// ── Factory ───────────────────────────────────────────────────────────

export function createCameraStrategy(cameraService: CameraService): CameraStrategy {
  if (isCapacitorNative() && isPlatformIOS()) {
    return new NativePluginCameraStrategy('NativeCameraStrategy');
  }
  if (isCapacitorNative() && isPlatformAndroid()) {
    return new NativePluginCameraStrategy('NativeAndroidCameraStrategy');
  }
  return new WebCameraStrategy(cameraService);
}

// ── Helpers ───────────────────────────────────────────────────────────

/** Derive getUserMedia `MediaTrackConstraints` from a camera-view's aspect
 *  ratio + ideal resolution. Exported so `cs-camera-view`'s web-only code
 *  paths (switch-camera freeze+flip, capture retries) can reuse the same
 *  logic without duplicating it. */
export function buildWebConstraints(
  aspectRatio: '1:1' | '4:3' | 'auto',
  idealResolution: number,
): MediaTrackConstraints {
  if (!idealResolution) return {};
  if (aspectRatio === '1:1') {
    return { width: { ideal: idealResolution }, height: { ideal: idealResolution } };
  }
  if (aspectRatio === '4:3') {
    return { width: { ideal: idealResolution }, height: { ideal: Math.round(idealResolution * 3 / 4) } };
  }
  return { width: { ideal: idealResolution } };
}

async function resizeBase64ToLimit(dataUrl: string, maxImageSize: number): Promise<{ dataUrl: string; base64: string }> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  canvas.getContext('2d').drawImage(img, 0, 0);
  return ensureImageSizeAsync(canvas, maxImageSize);
}
